import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { Divider, Grid } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import moment from 'moment';
import _ from 'lodash';

import useNotification from '../../../../../../core/hooks/use_notification';
import { Button, SubmitButton } from '../../../../../common/button';
import TextFieldFormik from '../../../../../common/text_field';
import { getFoodPartnersForReservationState } from '../../../../../../features/food_partner/get_food_partners_for_reservation';
import AutocompleteMultipleSearchOnType, { AutocompleteFormik } from '../../../../../common/autocomplete';
import { getProductsForReservationAction } from '../../../../../../features/product/get_products_for_reservation';
import { createProductAvailabilityNotificationData, getProductsForReservationNotificationData } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';
import { createProductAvailabilityAction, createProductAvailabilityState } from '../../../../../../features/reservation/create_product_availability';
import { parseDateString } from '../../../../../../core/utils/date_utils';
import DatePickerFormik from '../../../../../common/date_picker';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT } from '../../../../../../core/constants';

const ProductAvailabilityForm = ({ data, onClose, setData, setView, refreshCalendar, initialDate }) => {
    const dispatch = useDispatch();
    const { showNotification, showErrorMessage } = useNotification();

    const getFoodPartnersForReservation = useSelector(getFoodPartnersForReservationState);
    const createProductAvailability = useSelector(createProductAvailabilityState);

    const [productOptions, setProductOptions] = useState([]);
    const [loadingProductsOptions, setLoadingProductsOptions] = useState(false);
    const [chosenProducts, setChosenProducts] = useState(data?.formik?.chosenProducts || []);
    const [foodPartner, setFoodPartner] = useState(data?.formik?.foodPartner);

    const onProductsInputChange = (product) => {
        if (product.length >= 3) {
            setProductOptions([]);
            setLoadingProductsOptions(true);
            getProductsDebounced(product);
        }
    };

    const getProductsDebounced = _.debounce((query) => {
        dispatch(getProductsForReservationAction({ foodPartnerId: foodPartner?.value, query }))
            .then(response => {
                showErrorMessage(getProductsForReservationNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    const newProductOptions = mapProductOptions(response?.payload).concat(chosenProducts);
                    const newDistinctProductOptions = [...new Map(newProductOptions.map((option) => [option['value'], option])).values()];
                    setProductOptions(newDistinctProductOptions);
                }
                setLoadingProductsOptions(false);
            });
    }, 600);

    const mapFoodPartnerOptions = useCallback((optionsData) => {
        if (!optionsData) return [];
        return optionsData?.map(partner => ({
            key: partner?.id + Math.random(),
            value: partner?.id,
            label: partner?.name
        }));
    }, []);

    const mapProductOptions = useCallback((optionsData) => {
        if (!optionsData) return [];
        return optionsData?.map(product => {
            const isInactive = product?.active === false;
            const inactiveSuffix = isInactive ? ' (Nieaktywny)' : '';

            return {
                key: product?.id + Math.random(),
                value: product?.id,
                label: `${product?.name}${inactiveSuffix}`
            };
        });
    }, []);

    const initialValues = {
        foodPartner: data?.formik?.foodPartner || null,
        fromDate: data?.formik?.fromDate || (initialDate ? moment(initialDate) : null),
        toDate: data?.formik?.toDate || (initialDate ? moment(initialDate) : null),
        generalQuantityLimit: data?.formik?.generalQuantityLimit || ''
    };

    const schema = Yup.object().shape({
        foodPartner: Yup.object().required(),
        fromDate: Yup.date()
            .transform(parseDateString)
            .nullable()
            .required(),
        toDate: Yup.date()
            .transform(parseDateString)
            .nullable()
            .required(),
        generalQuantityLimit: Yup.number().notRequired()
    });

    const onFoodPartnerChange = (partner, formik) => {
        setFoodPartner(partner);
        formik.setFieldValue('productIds', '');
    };

    const onSubmit = (values) => {
        const productIds = chosenProducts.map(product => product.value);
        const foodPartnerId = _.pick(values, ['foodPartner'])?.foodPartner?.value || '';
        const fromDate = values?.fromDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT);
        const toDate = values?.toDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT);

        const form = {
            ..._.omit(values, ['foodPartner', 'fromDate', 'toDate']),
            productIds,
            foodPartnerId,
            fromDate,
            toDate
        };

        setData({
            form,
            formik: { ...values, foodPartner, chosenProducts }
        });

        if (values?.generalQuantityLimit && values?.generalQuantityLimit > 0) {
            setView(1);
        } else {
            dispatch(createProductAvailabilityAction(form))
                .then(response => {
                    showNotification(createProductAvailabilityNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        onClose();
                        refreshCalendar && refreshCalendar();
                    }
                });
        }
    };

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ onSubmit }>
            { (formik) => (
                <Form>
                    <Grid container spacing={ 3 }>
                        <Grid item xs={ 12 } md={ 4 }>
                            <AutocompleteFormik
                                name="foodPartner"
                                label="Dostawca"
                                options={ mapFoodPartnerOptions(getFoodPartnersForReservation?.data) }
                                required
                                onChange={ (value) => onFoodPartnerChange(value, formik) }
                            />
                        </Grid>
                        {
                            formik.values.foodPartner && (
                                <Grid item xs={ 12 } md={ 8 }>
                                    <AutocompleteMultipleSearchOnType
                                        options={ productOptions || [] }
                                        value={ chosenProducts }
                                        setValue={ (newValue) => setChosenProducts(newValue) }
                                        label="Produkty - wiele (wpisz min. 3 litery)"
                                        required
                                        onInputChange={ (value) => onProductsInputChange(value) }
                                        loading={ loadingProductsOptions }
                                    />
                                </Grid>
                            )
                        }
                        <Grid item xs={ 12 } sm={ 4 }>
                            <DatePickerFormik
                                name="fromDate"
                                label="Data od"
                                required
                                InputLabelProps={ { shrink: true } }
                                minDate={ moment() }
                            />
                        </Grid>
                        <Grid item xs={ 12 } sm={ 4 }>
                            <DatePickerFormik
                                name="toDate"
                                label="Data do"
                                required
                                InputLabelProps={ { shrink: true } }
                                minDate={ moment() }
                            />
                        </Grid>
                        <Grid item xs={ 12 } sm={ 4 }>
                            <TextFieldFormik
                                name="generalQuantityLimit"
                                type="number"
                                label="Globalny limit rezerwacji"
                                inputProps={ { min: '1', step: '1' } }
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={ { mt: 3, mb: 2, mx: -3 } }/>

                    <DialogActions sx={ { padding: 0, gap: 1 } }>
                        <Button variant="outlined" onClick={ onClose }>Zamknij</Button>
                        <SubmitButton isLoading={ createProductAvailability?.loading }>
                            { (formik?.values?.generalQuantityLimit && formik?.values?.generalQuantityLimit > 0) ? 'Dalej' : 'Zapisz' }
                        </SubmitButton>
                    </DialogActions>
                </Form>
            ) }
        </Formik>
    );
};

export default ProductAvailabilityForm;